import { useState, useRef, useEffect } from "react";
import DescriptionModal from "./DescriptionModal";
import ErrorModal from "../common/ErrorModal";

const CONSTANT = 11.664;

export default function TradeForm({
  type,
  headerData,
  onAddItem,
  formData,
  setFormData,
}) {
  const isBuy = type === "buy";

  const [descriptionModal, setDescriptionModal] = useState(false);
  const lastEditedRef = useRef(null);
  const [errors, setErrors] = useState([]);

  // =========================
  // ✅ HANDLE CHANGE
  // =========================
  const handleChange = (e) => {
    const { name, value } = e.target;

    // Update ref for weight or amount fields
    if (name === "weight" || name === "amount") {
      lastEditedRef.current = name;
    }

    setFormData((prev) => {
      let updated = { ...prev, [name]: value };

      const weight = parseFloat(updated.weight) || 0;
      const rate = parseFloat(updated.rate) || 0;
      const amount = parseFloat(updated.amount) || 0;
      const diff = parseFloat(updated.diff) || 0;

      // =========================
      // ✅ R/W LOGIC (Updated)
      // =========================
      if (name === "rwType") {
        if (value === "Ready") {
          updated.status = "D&L";
          updated.postDate = new Date();
        } else if (value === "Week") {
          const nextWeek = new Date();
          nextWeek.setDate(nextWeek.getDate() + 7);
          updated.status = "Pending";
          updated.postDate = nextWeek.toISOString().split("T")[0];
        } else if (value === "Pending") {
          updated.status = "Pending";
          updated.postDate = "";
        }
      }

      // =========================
      // ✅ AUTO CALC LOGIC
      // =========================
      const lastEdited = lastEditedRef.current;

      if (lastEdited === "weight" || name === "weight") {
        // Weight is driving → calculate amount
        if (weight > 0 && rate > 0) {
          const baseAmount = (weight / CONSTANT) * rate;
          updated.amount = baseAmount.toFixed(2);
          updated.originalAmount = baseAmount;
        }
      } else if (lastEdited === "amount" || name === "amount") {
        // Amount is driving → calculate weight
        if (name === "amount") {
          updated.originalAmount = amount;
        }
        if (amount > 0 && rate > 0) {
          updated.weight = ((amount * CONSTANT) / rate).toFixed(3);
        }
      }

      // =========================
      // ✅ DIFF LOGIC
      // =========================
      const originalAmount =
        parseFloat(updated.originalAmount) ||
        (weight > 0 && rate > 0 ? (weight / CONSTANT) * rate : amount);

      if (diff > 0 && weight > 0 && originalAmount > 0) {
        const diffResult = (weight / CONSTANT) * diff;
        updated.amount = isBuy
          ? (originalAmount - diffResult).toFixed(2)
          : (originalAmount + diffResult).toFixed(2);
      }

      return updated;
    });
  };

  // =========================
  // ✅ SUBMIT
  // =========================
  const handleClear = () => {
    setFormData({
      description: "",
      weight: "",
      rwType: "Ready",
      rate: "",
      amount: "",
      status: "D&L",
      diff: "",
      postDate: new Date(),
      originalAmount: "",
    });
  };

  return (
    <>
      <div className="card trade-card">
        <div className="card-header trade-header">
          {isBuy ? "Buy Entry" : "Sell Entry"}
        </div>

        <div className="card-body">
          <form>
            {/* Description */}
            <div className="field-row">
              <div className="label light main">Description</div>
              <div className="description-input-wrapper">
                <textarea
                  className="input dark description-input"
                  rows={1}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                />
                <i
                  className="fa-solid fa-pencil edit-icon"
                  onClick={() => setDescriptionModal(true)}
                  style={{ cursor: "pointer" }}
                />
              </div>
            </div>

            {/* Weight */}
            <div className="field-row">
              <div className="label light main">Weight</div>
              <input
                type="number"
                name="weight"
                className="input dark"
                value={formData.weight}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
              />
            </div>

            {/* R/W + Rate */}
            <div className="field-row small-row">
              <div className="label dark">R (or) W</div>
              <select
                name="rwType"
                className="input light"
                value={formData.rwType}
                onChange={handleChange}
              >
                <option value="Ready">Ready</option>
                <option value="Week">Week</option>
                <option value="Pending">Pending</option>
              </select>
              <div className="label dark">Rate</div>
              <input
                type="number"
                name="rate"
                className="input light"
                value={formData.rate}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
              />
            </div>

            {/* Amount */}
            <div className="field-row">
              <div className="label dark">Amount</div>
              <input
                type="number"
                name="amount"
                className="input light long"
                value={formData.amount}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
              />
            </div>

            {/* Status */}
            <div className="field-row long">
              <div className="label light main">Status</div>
              <select
                name="status"
                className="input dark long"
                value={formData.status}
                onChange={handleChange}
                disabled={true}
              >
                <option value="Pending">Pending</option>
                <option value="D&L">D&L</option>
              </select>
            </div>

            {/* Diff */}
            <div className="field-row">
              <div className="label dark">Diff</div>
              <input
                type="number"
                name="diff"
                className="input light long"
                value={formData.diff}
                onChange={handleChange}
                onWheel={(e) => e.target.blur()}
              />
            </div>

            {/* Post Date */}
            {formData.rwType !== "Ready" && (
              <>
                <div className="field-row">
                  <div className="label light main" id="Post-Date">
                    Post Date
                  </div>
                </div>
                <div className="field-row">
                  <div className="label dark">Post Date</div>
                  <input
                    type="date"
                    name="postDate"
                    className="input light long"
                    value={formData.postDate}
                    onChange={handleChange}
                    disabled={formData.rwType === "Week"}
                    required={formData.rwType === "Pending"}
                  />
                </div>
              </>
            )}

            {/* Submit */}
            <div className="container" id="button">
              <div className="row mt-4 d-flex justify-content-between">
                <div
                  className="btn btn-light"
                  style={{ width: "100px" }}
                  type="clear"
                  onClick={handleClear}
                >
                  Clear
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>

      <DescriptionModal
        show={descriptionModal}
        onClose={() => setDescriptionModal(false)}
        onSave={(description) =>
          setFormData((prev) => ({ ...prev, description }))
        }
      />
    </>
  );
}
